import React, { useEffect, useState } from "react";
import axios from "axios";
import { ArrowLeft } from "lucide-react";
import { countryName } from "../lib/countries";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (v) => {
  if (v == null) return "—";
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `$${Math.round(v / 1_000)}k`;
  return `$${Math.round(v)}`;
};

const SORTS = [
  { key: "revenue", label: "Total revenue" },
  { key: "count", label: "# products" },
  { key: "growth", label: "Avg growth" },
];

const DIFF = {
  Healthy: "bg-emerald-100 text-emerald-700",
  Moderate: "bg-sky-100 text-sky-700",
  Hard: "bg-amber-100 text-amber-700",
  Brutal: "bg-rose-100 text-rose-700",
};

const DiffBadge = ({ d }) =>
  d ? <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${DIFF[d] || "bg-zinc-100 text-zinc-600"}`}>{d} to monetize</span> : null;

const Breakdown = ({ c }) =>
  c.tried ? (
    <div className="text-[11px] text-zinc-500 mt-2">
      <b className="text-emerald-700">{c.winners} earning</b> · {c.attempts} pre-revenue · <b className="text-rose-600">{c.graveyard} died trying</b>
      <span className="text-zinc-400"> — {Math.round((c.monetization_rate || 0) * 100)}% ever reach $50+/mo</span>
    </div>
  ) : null;

/** Discover → Bootstrapped: browse proven money-making categories (TrustMRR). */
export default function BootstrappedCategories() {
  const [cats, setCats] = useState([]);
  const [sort, setSort] = useState("revenue");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/bootstrapped/categories`, { params: { sort } })
      .then((r) => setCats(r.data.categories || []))
      .finally(() => setLoading(false));
  }, [sort]);

  useEffect(() => {
    if (!selected) { setDetail(null); return; }
    setDetail(null);
    axios.get(`${API}/bootstrapped/categories/${encodeURIComponent(selected)}`).then((r) => setDetail(r.data)).catch(() => {});
  }, [selected]);

  if (selected) return <CatDetail detail={detail} onBack={() => setSelected(null)} />;

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6" data-testid="bootstrapped-categories">
      <div className="text-xs font-bold tracking-widest text-emerald-600 mb-2">DISCOVER · PROVEN MONEY-MAKING CATEGORIES</div>
      <h1 className="text-3xl sm:text-4xl font-extrabold text-zinc-900 mb-2">Where indie founders actually make money.</h1>
      <p className="text-zinc-500 max-w-3xl">
        {cats.length} categories ranked by real, self-reported revenue (via TrustMRR) — each backed by live bootstrapped products and the markets still open to clone into.
      </p>

      <div className="flex items-center gap-2 mt-6 mb-5">
        <span className="text-xs text-zinc-400">Sort</span>
        <select value={sort} onChange={(e) => setSort(e.target.value)} className="px-3 py-2 rounded-lg border border-zinc-200 text-sm" data-testid="bootstrapped-cat-sort">
          {SORTS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="text-zinc-400 text-sm py-10">Loading…</div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-3">
          {cats.map((c) => (
            <button
              key={c.category}
              onClick={() => setSelected(c.category)}
              className="text-left p-4 rounded-xl border border-zinc-200 bg-white hover:border-emerald-300 transition-colors min-w-0 w-full overflow-hidden"
              data-testid={`bootstrapped-category-${c.category}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="font-semibold text-zinc-900">{c.category}</div>
                <div className="text-right"><div className="font-bold text-emerald-700">{fmt(c.total_mrr_usd)}<span className="text-[10px] text-emerald-500">/mo</span></div></div>
              </div>
              <div className="mt-1.5"><DiffBadge d={c.difficulty} /></div>
              <div className="text-xs text-zinc-500 mt-2">
                {c.product_count} earning · median {fmt(c.median_mrr_usd)}/mo
                {c.avg_growth_30d != null && <> · avg {c.avg_growth_30d > 0 ? "+" : ""}{c.avg_growth_30d}% revenue growth (30d)</>}
              </div>
              <Breakdown c={c} />
              <div className="text-[11px] text-zinc-400 mt-2 truncate">Top: {c.top_earners.map((t) => `${t.name} (${fmt(t.monthly_usd)})`).join(" · ")}</div>
              {c.open_markets.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  <span className="text-[10px] text-zinc-400">open:</span>
                  {c.open_markets.slice(0, 6).map((m) => <span key={m} className="px-1.5 py-0.5 rounded bg-violet-50 text-violet-700 text-[10px] font-semibold">{countryName(m)}</span>)}
                </div>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const CatDetail = ({ detail, onBack }) => {
  if (!detail) return <div className="max-w-4xl mx-auto p-6 text-zinc-400">Loading…</div>;
  if (detail.error) return <div className="max-w-4xl mx-auto p-6 text-zinc-500">Not found.</div>;
  return (
    <div className="max-w-4xl mx-auto p-4 sm:p-6" data-testid="bootstrapped-category-detail">
      <button onClick={onBack} data-testid="bootstrapped-back" className="flex items-center gap-1 text-sm text-zinc-500 hover:text-zinc-900 mb-4"><ArrowLeft size={15} /> Back to categories</button>
      <h1 className="text-2xl font-extrabold text-zinc-900">{detail.category}</h1>
      <div className="text-sm text-zinc-500 mt-1 mb-4">
        {detail.product_count} revenue-proven products · total {fmt(detail.total_mrr_usd)}/mo · median {fmt(detail.median_mrr_usd)}/mo
      </div>

      {detail.health && detail.health.tried > 0 && (
        <div className="rounded-xl border border-zinc-200 bg-white p-4 mb-5" data-testid="category-health">
          <div className="flex items-center justify-between gap-3 mb-2">
            <div className="text-sm font-bold text-zinc-900">How hard is this category to monetize?</div>
            <DiffBadge d={detail.health.difficulty} />
          </div>
          <p className="text-xs text-zinc-500 mb-3">
            Across all {detail.health.tried} products our data has ever tracked in {detail.category},
            only <b className="text-zinc-800">{Math.round(detail.health.monetization_rate * 100)}%</b> reached $50+/mo.
          </p>
          <div className="flex gap-2 text-center text-xs">
            <div className="flex-1 rounded-lg bg-emerald-50 py-2"><div className="font-bold text-emerald-700">{detail.health.winners}</div><div className="text-[10px] text-zinc-500">earning</div></div>
            <div className="flex-1 rounded-lg bg-zinc-50 py-2"><div className="font-bold text-zinc-600">{detail.health.attempts}</div><div className="text-[10px] text-zinc-500">pre-revenue</div></div>
            <div className="flex-1 rounded-lg bg-rose-50 py-2"><div className="font-bold text-rose-600">{detail.health.graveyard}</div><div className="text-[10px] text-zinc-500">died trying</div></div>
          </div>
        </div>
      )}

      <div className="rounded-xl border border-zinc-200 bg-white p-4 mb-5">
        <div className="text-sm font-bold text-zinc-900 mb-2">Markets still open to clone into</div>
        {detail.clone_angle.open_markets.length ? (
          <div className="flex flex-wrap gap-2">{detail.clone_angle.open_markets.map((m) => <span key={m} className="px-2.5 py-1 rounded-full bg-violet-50 text-violet-700 text-xs font-semibold">{countryName(m)}</span>)}</div>
        ) : <div className="text-xs text-zinc-500">This category is already sold in every major market — there's no open geographic gap, so compete on a sharper wedge or price instead.</div>}
      </div>

      <div className="space-y-2">
        {detail.products.map((p) => (
          <div key={p.id} className="flex items-center justify-between gap-3 p-3 rounded-lg border border-zinc-200 bg-white">
            <div className="min-w-0"><div className="text-sm font-semibold text-zinc-900 truncate">{p.name}</div><div className="text-[11px] text-zinc-500 truncate">{[countryName(p.country)].filter(Boolean).join(" · ")}{p.description ? ` — ${p.description.slice(0, 90)}` : ""}</div></div>
            <div className="text-sm font-bold text-emerald-700 flex-shrink-0">{fmt(p.monthly_usd)}<span className="text-[10px] text-emerald-500">/mo</span></div>
          </div>
        ))}
      </div>
    </div>
  );
};
